import { type DateTimeValueReturnType, getDateRangeISO, reshapeSelectedFilters, sanitizeObjectEntries } from 'BreetComponents';
import { type DropdownOptionType, getQueryParamValue, type IPagination, updateUrlSearchParams } from 'BreetHelpers';
import { type ComponentType, useCallback, useEffect, useMemo, useState } from 'react';

import type { WithPaginatedFiltersProps } from './helpers';

const sortableFilters = ['createdAt', 'updatedAt'];

export const withPaginatedFilters = <P, D = object | undefined>(
	Component: ComponentType<P>,
	extraOptions: { extraSortableFilters?: string[]; initialDateRange?: DateTimeValueReturnType } = {
		extraSortableFilters: [],
		initialDateRange: getDateRangeISO(1),
	}
) => {
	const EnhacedComponent = (props: P & Partial<WithPaginatedFiltersProps<D>>) => {
		const initialDateRange = extraOptions.initialDateRange ?? getDateRangeISO(1);
		const extraSortableFilters = useMemo(() => extraOptions.extraSortableFilters ?? [], []);

		const [reqPageNum, setReqPageNum] = useState(1);
		const [reqPageSize, setReqPageSize] = useState(50);
		const [reqFilter, setReqFilter] = useState<Record<string, string>>({});
		const [reqSearch, setReqSearch] = useState<Record<string, string>>({});
		const [reqSorting, setReqSorting] = useState<Record<string, string>>({});
		const [checkedItems, setCheckedItems] = useState<D[]>([]);
		const [reqDateRange, setReqDateRange] = useState<DateTimeValueReturnType>(initialDateRange);

		useEffect(() => {
			const page = getQueryParamValue('page');
			const size = getQueryParamValue('size');

			if (page) setReqPageNum(+page);
			if (size) setReqPageSize(+size);
		}, []);

		const handleCheckboxChange = useCallback((data: D[]) => {
			setCheckedItems(data);
		}, []);

		const handlePageChange = useCallback((state: Partial<IPagination>) => {
			setReqPageNum(state.currentPage ?? 1);
			setReqPageSize(state.pageLimit ?? 10);
			updateUrlSearchParams({ addParams: { page: state.currentPage ?? 1, size: state.pageLimit ?? 10 } });
		}, []);

		const handleSelectedFilterChange = useCallback(
			(selectedFilters?: Record<string, DropdownOptionType | undefined>) => {
				if (!selectedFilters) return;

				const sortingFilters: Record<string, DropdownOptionType> = {};
				const otherFilters: Record<string, DropdownOptionType> = {};

				Object.entries(selectedFilters).forEach(([filteredKey, filteredValue]) => {
					if (!filteredValue) return;
					if ([...sortableFilters, ...extraSortableFilters].includes(filteredKey)) sortingFilters[filteredKey] = filteredValue;
					else otherFilters[filteredKey] = filteredValue;
				});

				setReqSorting(reshapeSelectedFilters(sortingFilters));
				setReqFilter(reshapeSelectedFilters(otherFilters));

				setReqPageNum(1);
			},
			[extraSortableFilters]
		);

		const updateFilterData = (
			prevData: Record<string, string>,
			currentSearchKey: string,
			sanitizedData: Record<string, string>
		) => {
			const restOfPrevData = Object.fromEntries(Object.entries(prevData).filter(([key]) => key !== currentSearchKey));
			return { ...restOfPrevData, ...sanitizedData } as Record<string, string>;
		};

		const handleReqSearch = useCallback((data: Record<string, string>) => {
			const sanitizedData = sanitizeObjectEntries(data);
			const searchKeys = Object.keys(data);
			const [currentSearchKey] = searchKeys;
			const strictFilterKeys = ['_id'];
			const searchIncludesFilterKey = searchKeys.some((key) => strictFilterKeys.includes(key));

			const encodedSearch = Object.entries(sanitizedData).reduce<Record<string, string>>((acc, [key, value]) => {
				acc[key] = encodeURIComponent(value as string);
				return acc;
			}, {});

			if (searchIncludesFilterKey) {
				setReqFilter((prevData) => updateFilterData(prevData, currentSearchKey, sanitizedData as Record<string, string>));
			} else {
				setReqSearch(encodedSearch);
			}

			setReqPageNum(1);
		}, []);

		const handleDateTimeChange = useCallback((options: DateTimeValueReturnType) => {
			setReqDateRange(options);
		}, []);

		const exportsQuery = useMemo(
			() => ({
				startDate: reqDateRange.startDate,
				endDate: reqDateRange.endDate,
				exports: true,
			}),
			[reqDateRange]
		);

		return (
			<Component
				{...props}
				reqPageNum={reqPageNum}
				reqPageSize={reqPageSize}
				reqFilter={reqFilter}
				reqSearch={reqSearch}
				reqSorting={reqSorting}
				reqDateRange={reqDateRange}
				checkedItems={checkedItems}
				handleCheckboxChange={handleCheckboxChange}
				handlePageChange={handlePageChange}
				handleSelectedFilterChange={handleSelectedFilterChange}
				handleReqSearch={handleReqSearch}
				handleDateTimeChange={handleDateTimeChange}
				exportsQuery={exportsQuery}
			/>
		);
	};

	return EnhacedComponent;
};
